import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Settings, CheckCircle, Target, TrendingUp, Users } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

export default function ProzessoptimierungPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "Prozessoptimierung für KMU | LAB336 – Effizienz steigern",
    description: "Professionelle Prozessoptimierung für Schweizer KMU. Wir analysieren Ihre Geschäftsprozesse, identifizieren Engpässe und implementieren Lean-Methoden für nachhaltige Effizienz.",
    canonical: `${BASE_URL}/prozessoptimierung`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Prozessoptimierung",
          "description": "Analyse und Neugestaltung von Geschäftsprozessen für maximale Effizienz",
          "provider": { "@type": "Organization", "name": "LAB336", "url": BASE_URL },
          "areaServed": { "@type": "Country", "name": "Schweiz" },
          "serviceType": "Prozessoptimierung"
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": "Prozessoptimierung", "item": `${BASE_URL}/prozessoptimierung` }
          ]
        }
      ]
    }
  });

  const goToScheduler = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#scheduler");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="service-page-prozessoptimierung">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
              <Settings className="w-8 h-8 text-lab-accent" strokeWidth={1.5} />
            </div>
            <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Leistung</span>
            <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-lab-primary mt-4 max-w-3xl">
              Prozessoptimierung für Schweizer KMU
            </h1>
            <p className="text-lab-text-muted text-lg mt-6 max-w-2xl leading-relaxed">
              Ihre Abläufe kosten Sie mehr als nötig. Wir finden heraus, wo — und verändern es. Messbar, nachhaltig und ohne Ihr Tagesgeschäft zu blockieren.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nutzenargumentation – Fliesstext */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-6">Warum Prozessoptimierung entscheidend ist</h2>
            <div className="text-lab-text-muted leading-relaxed space-y-4">
              <p>
                Viele Schweizer KMU arbeiten mit Abläufen, die über Jahre gewachsen sind — aber nie hinterfragt wurden. Das Ergebnis: Aufgaben werden doppelt erledigt, Informationen gehen zwischen Abteilungen verloren, und Mitarbeitende verbringen Stunden mit Tätigkeiten, die keinen Mehrwert schaffen. Das kostet nicht nur Geld, sondern frustriert auch Ihr Team.
              </p>
              <p>
                Die Herausforderung ist vielen Geschäftsführern bewusst — doch im Tagesgeschäft fehlt die Zeit, das Problem systematisch anzugehen. Genau hier setzen wir an: Als externe Spezialisten bringen wir den Blick von aussen mit, den es braucht, um eingefahrene Muster zu durchbrechen.
              </p>
              <p>
                Wir arbeiten nicht mit theoretischen Modellen, sondern direkt an Ihren realen Prozessen. Gemeinsam mit Ihrem Team dokumentieren wir den Ist-Zustand, messen Durchlaufzeiten und identifizieren die Stellen, an denen der grösste Hebel liegt. Daraus entwickeln wir einen konkreten Massnahmenplan — priorisiert nach Aufwand und Wirkung. Die Umsetzung begleiten wir Schritt für Schritt, damit Veränderungen nicht nur auf dem Papier stehen, sondern im Alltag ankommen.
              </p>
              <p>
                Das Resultat: Ihre Abläufe werden schneller, Ihre Kosten sinken und Ihr Team kann sich auf die Arbeit konzentrieren, die wirklich zählt. Und das Beste — die Verbesserungen sind dauerhaft, weil wir Ihr Team befähigen, den Optimierungsprozess eigenständig weiterzuführen.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">Konkrete Szenarien aus der Praxis</h2>
            <p className="text-lab-text-muted mb-10">So sieht Prozessoptimierung im KMU-Alltag aus:</p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Handelsbetrieb mit 25 Mitarbeitenden",
                  problem: "Bestellungen wurden per E-Mail, Telefon und Excel verwaltet — mit Fehlerquoten von 12%.",
                  solution: "Zentralisierter Bestellprozess, automatische Auftragsbestätigung.",
                  result: "Fehlerquote auf 2% gesenkt, 15 Stunden/Woche eingespart."
                },
                {
                  title: "Dienstleister mit 8 Mitarbeitenden",
                  problem: "Projektübergaben zwischen Vertrieb und Umsetzung dauerten Tage, Informationen gingen verloren.",
                  solution: "Strukturiertes Übergabeprotokoll mit digitalem Workflow.",
                  result: "Übergabezeit von 3 Tagen auf 2 Stunden reduziert."
                },
                {
                  title: "Produktionsbetrieb mit 40 Mitarbeitenden",
                  problem: "Materialbestellungen wurden manuell ausgelöst — oft zu spät oder in falscher Menge.",
                  solution: "Automatische Bestellauslösung bei Mindestbestand.",
                  result: "Lagerkosten um 30% gesenkt, keine Produktionsstopps mehr."
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-lab-border p-6">
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Problem:</span> {item.problem}</p>
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Lösung:</span> {item.solution}</p>
                    <p className="text-lab-accent font-medium"><CheckCircle className="w-4 h-4 inline mr-1" />{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Was wir tun */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Unsere Leistungen im Detail</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: Target, title: "Prozessanalyse & Mapping", text: "Wir kartieren Ihre bestehenden Abläufe, messen Durchlaufzeiten und identifizieren versteckte Ineffizienzen — datenbasiert und transparent. Sie sehen schwarz auf weiss, wo Ihr Unternehmen Zeit und Geld verliert." },
                { icon: TrendingUp, title: "Lean Management", text: "Mit bewährten Lean-Methoden eliminieren wir Verschwendung und schaffen schlanke Workflows. Keine Theorie — wir setzen direkt an Ihren Engpässen an und liefern schnelle, spürbare Verbesserungen." },
                { icon: Settings, title: "Workflow-Automatisierung", text: "Repetitive Aufgaben automatisieren wir mit modernen Tools — von einfachen Makros bis hin zu intelligenten Workflow-Engines. Ihr Team wird von Routinearbeit befreit und kann sich auf wertschöpfende Tätigkeiten konzentrieren." },
                { icon: Users, title: "Change Management", text: "Prozessänderungen gelingen nur mit den Menschen. Wir begleiten Ihr Team durch den Wandel mit Schulungen, klarer Kommunikation und praxisnahen Workshops — damit Veränderungen akzeptiert und gelebt werden." },
              ].map((item) => (
                <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                  className="bg-lab-bg-light p-8 border border-lab-border">
                  <item.icon className="w-6 h-6 text-lab-accent mb-4" strokeWidth={1.5} />
                  <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <p className="text-lab-text-muted leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="bg-lab-bg-dark py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-12">Unser Vorgehen in 4 Schritten</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Analyse", text: "Ist-Zustand erfassen, Prozesse dokumentieren und Kennzahlen messen — gemeinsam mit Ihrem Team vor Ort." },
              { step: "02", title: "Konzept", text: "Optimierungspotenziale bewerten, Massnahmen priorisieren und eine umsetzbare Roadmap erstellen." },
              { step: "03", title: "Umsetzung", text: "Neue Prozesse implementieren, Automatisierungen einrichten und Mitarbeitende gezielt schulen." },
              { step: "04", title: "Kontrolle", text: "Ergebnisse messen, KPIs überwachen und sicherstellen, dass Verbesserungen langfristig wirken." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-white">
                <span className="text-lab-accent text-4xl font-playfair font-semibold">{item.step}</span>
                <h3 className="font-playfair text-xl font-semibold mt-4 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ergebnisse */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Typische Ergebnisse unserer Kunden</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { metric: "30-50%", label: "Weniger Durchlaufzeit" },
              { metric: "20-40%", label: "Kosteneinsparung" },
              { metric: "90%+", label: "Mitarbeiterzufriedenheit nach der Umstellung" },
            ].map((item) => (
              <div key={item.label} className="text-center p-8 bg-lab-bg-light border border-lab-border">
                <span className="text-4xl font-playfair font-semibold text-lab-accent">{item.metric}</span>
                <p className="text-lab-text-muted mt-2">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lab-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-4">
            Ihre Prozesse kosten Sie mehr, als Sie denken
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            In einem kostenlosen 30-Minuten-Erstgespräch analysieren wir gemeinsam, wo in Ihrem Unternehmen die grössten Optimierungspotenziale liegen — konkret, unverbindlich und auf Ihre Branche zugeschnitten.
          </p>
          <p className="text-lab-accent font-medium mb-8">
            Kein Verkaufsgespräch. Kein Kleingedrucktes. Nur eine ehrliche Einschätzung, ob und wie wir Ihnen helfen können.
          </p>
          <button onClick={goToScheduler} className="bg-lab-accent text-lab-primary font-semibold px-10 py-4 text-base inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="cta-scheduler">
            Kostenloses Erstgespräch buchen <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
